import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Shield, Crown, Sparkles, User as UserIcon, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { WorkspaceShell } from '../components/layout';
import { useAuth } from '../contexts/AuthContext';
import {
  TierId,
  DurationId,
  TIERS,
  DURATIONS,
  getPrice,
  getMonthlyDiscountPercent,
  formatTierLabel,
} from '../constants/subscriptionPlans';
import { formatCompactNumber } from '../utils/formatNumber';

type AllTierId = 'free' | TierId;

const ALL_TIER_ICONS: Record<AllTierId, React.ReactNode> = {
  free: <UserIcon className="h-5 w-5" />,
  plus: <Sparkles className="h-5 w-5" />,
  max: <Shield className="h-5 w-5" />,
  ultra: <Crown className="h-5 w-5" />,
};

const ALL_TIER_EXTRA_FEATURES: Record<AllTierId, string[]> = {
  free: ['plans.community_support'],
  plus: ['plans.priority_support'],
  max: ['plans.extended_context', 'plans.tools_calling', 'plans.priority_support'],
  ultra: ['plans.priority', 'plans.max_context', 'plans.tools_calling', 'plans.dedicated_support'],
};

const ALL_TIER_STYLES: Record<AllTierId, { gradient: string; border: string; activeBorder: string; badge: string; buttonInactive: string; checkmark: string }> = {
  free: {
    gradient: 'from-card to-card',
    border: 'border-border hover:border-foreground/20',
    activeBorder: 'border-primary ring-1 ring-primary',
    badge: 'bg-muted text-foreground',
    buttonInactive: 'bg-background text-foreground border border-input hover:bg-accent hover:text-accent-foreground',
    checkmark: 'text-foreground/70',
  },
  plus: {
    gradient: 'from-card to-card',
    border: 'border-border hover:border-sky-500/30',
    activeBorder: 'border-sky-500 ring-1 ring-sky-500 shadow-lg shadow-sky-500/10',
    badge: 'bg-sky-500/10 text-sky-500',
    buttonInactive: 'bg-sky-500 text-white hover:bg-sky-600 shadow-sm shadow-sky-500/20',
    checkmark: 'text-sky-500',
  },
  max: {
    gradient: 'from-card to-card',
    border: 'border-border hover:border-violet-500/30',
    activeBorder: 'border-violet-500 ring-1 ring-violet-500 shadow-lg shadow-violet-500/10',
    badge: 'bg-violet-500/10 text-violet-500',
    buttonInactive: 'bg-violet-500 text-white hover:bg-violet-600 shadow-sm shadow-violet-500/20',
    checkmark: 'text-violet-500',
  },
  ultra: {
    gradient: 'from-card to-card',
    border: 'border-border hover:border-amber-500/30',
    activeBorder: 'border-amber-500 ring-1 ring-amber-500',
    badge: 'bg-amber-500/10 text-amber-500',
    buttonInactive: 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20',
    checkmark: 'text-amber-500',
  },
};

import PageTitle from '../components/PageTitle';

function Plans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(['landing', 'common']);
  const [duration, setDuration] = useState<DurationId>('monthly');
  const [subscribing, setSubscribing] = useState<TierId | null>(null);

  const currencySymbol = '$';

  const activeTier = useMemo((): AllTierId | null => {
    if (!user) return null;
    if (!user.subscription || user.subscription.status !== 'active') return 'free';
    return (user.subscription.tier as TierId) || 'free';
  }, [user]);

  const handleSubscribe = (tierId: TierId) => {
    if (!user) {
      toast.error(t('plans.sign_in_first'));
      navigate('/auth?mode=login');
      return;
    }

    setSubscribing(tierId);
    navigate('/console');
  };

  return (
    <WorkspaceShell
      title={t('plans.title')}
      subtitle={t('plans.subtitle')}
      actions={(
        <div className="flex items-center bg-muted border border-border rounded-lg p-1">
          {DURATIONS.map(d => (
            <button
              key={d.id}
              onClick={() => setDuration(d.id)}
              className={`py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                duration === d.id
                  ? 'bg-card text-foreground shadow-xs border border-border'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('plans.duration_' + d.id)}
            </button>
          ))}
        </div>
      )}
    >
      <PageTitle 
        titleKey="plans.title" 
        namespace="landing" 
        descriptionKey="plans.subtitle"
      />

      <section className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* Free card */}
          {(() => {
            const isFreeActive = activeTier === 'free';
            return (
              <div className={`relative flex flex-col rounded-lg border ${isFreeActive ? ALL_TIER_STYLES.free.activeBorder : ALL_TIER_STYLES.free.border} bg-card p-6 transition-colors`}>
                {isFreeActive && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold tracking-wide uppercase flex items-center gap-1">
                    <Check className="h-3.5 w-3.5" /> {t('plans.current_plan')}
                  </div>
                )}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold tracking-tight">{formatTierLabel('free')}</h3>
                    <div className={`p-2 rounded-lg ${ALL_TIER_STYLES.free.badge}`}>
                      {ALL_TIER_ICONS.free}
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-semibold tracking-normal">{currencySymbol}0</span>
                    <span className="text-muted-foreground font-medium">{t('plans.forever')}</span>
                  </div>
                </div>

                <div className="mb-6">
                  {(() => {
                    const parts = t('plans.tier_desc_free').split(/\s*[:：、,]\s*/).filter(Boolean);
                    const [category, ...models] = parts;
                    return (
                      <div className="rounded-lg border border-border/60 p-3.5">
                        <p className="text-sm font-semibold text-foreground mb-2">{category}</p>
                        <ul className="space-y-1.5">
                          {models.map((m, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>

                <ul className="space-y-4 text-sm text-muted-foreground flex-1 mb-8">
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${ALL_TIER_STYLES.free.checkmark} shrink-0`} />
                    <span>{t('plans.messages_window', { count: 10, hours: 2 })}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${ALL_TIER_STYLES.free.checkmark} shrink-0`} />
                    <span>{t('plans.tokens_window', { tokens: '200K', hours: 2 })}</span>
                  </li>
                  {ALL_TIER_EXTRA_FEATURES.free.map(key => (
                    <li key={key} className="flex items-start gap-3">
                      <Check className={`h-5 w-5 ${ALL_TIER_STYLES.free.checkmark} shrink-0`} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => !user && navigate('/auth?mode=register')}
                  disabled={!!user}
                  className={`w-full h-12 inline-flex items-center justify-center rounded-md font-medium transition-all ${
                    isFreeActive
                      ? 'bg-muted text-muted-foreground cursor-default border border-border'
                      : ALL_TIER_STYLES.free.buttonInactive
                  } disabled:opacity-70`}
                >
                  {isFreeActive ? t('plans.current_plan') : t('plans.get_started')}
                </button>
              </div>
            );
          })()}

          {/* Paid tier cards */}
          {TIERS.map(tier => {
            const price = getPrice(tier.tier, duration);
            const discount = getMonthlyDiscountPercent(tier.tier, duration);
            const isActive = activeTier === tier.tier;
            const isPopular = tier.highlight;
            const tierStyle = ALL_TIER_STYLES[tier.tier];

            return (
              <div
                key={tier.tier}
                className={`relative flex flex-col rounded-lg border bg-card p-6 transition-colors ${
                  isActive ? tierStyle.activeBorder : isPopular ? tierStyle.activeBorder : tierStyle.border
                }`}
              >
                  {isActive && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold tracking-wide uppercase flex items-center gap-1">
                      <Check className="h-3.5 w-3.5" /> {t('plans.current_plan')}
                    </div>
                  )}
                
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold tracking-tight">{tier.label}</h3>
                    <div className={`p-2 rounded-lg ${tierStyle.badge}`}>
                      {ALL_TIER_ICONS[tier.tier]}
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-semibold tracking-normal">{currencySymbol}{price}</span>
                    <span className="text-muted-foreground font-medium">/{t('plans.duration_' + duration)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="mt-2.5">
                      <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-600 px-2.5 py-1 text-xs font-semibold">
                        {t('plans.save_percent', { percent: discount })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  {(() => {
                    const parts = t('plans.tier_desc_' + tier.tier).split(/\s*[:：、,]\s*/).filter(Boolean);
                    const [category, ...models] = parts;
                    return (
                      <div className="rounded-lg border border-border/60 p-3.5">
                        <p className="text-sm font-semibold text-foreground mb-2">{category}</p>
                        <ul className="space-y-1.5">
                          {models.map((m, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>

                <ul className="space-y-4 text-sm text-muted-foreground flex-1 mb-8">
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${tierStyle.checkmark} shrink-0`} />
                    <span>{t('plans.messages_window', { count: tier.usageLimit, hours: tier.windowHours })}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${tierStyle.checkmark} shrink-0`} />
                    <span>{t('plans.tokens_window', { tokens: formatCompactNumber(tier.tokenLimit), hours: tier.windowHours })}</span>
                  </li>
                  {ALL_TIER_EXTRA_FEATURES[tier.tier].map(key => (
                    <li key={key} className="flex items-start gap-3">
                      <Check className={`h-5 w-5 ${tierStyle.checkmark} shrink-0`} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(tier.tier)}
                  disabled={subscribing === tier.tier || isActive}
                  className={`w-full h-12 inline-flex items-center justify-center rounded-md font-medium transition-all ${
                    isActive
                      ? 'bg-muted text-muted-foreground cursor-default border border-border'
                      : tierStyle.buttonInactive
                  } disabled:opacity-70`}
                >
                  {isActive ? t('plans.current_plan') : subscribing === tier.tier ? t('common:processing') : t('plans.get_plan', { tier: tier.label })}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </WorkspaceShell>
  );
}

export default Plans;
